import React, { useState } from 'react';
import CommonButton from '../../../../Components/CommonButton';
import CommonTable from '../../../../Components/CommonTable';
import CommonActionMenu from '../../../../Components/CommonActionMenu';
import { useConfirm } from '../../../../Components/ConfirmModal';
import Section from './Section';
import {
    risk_portal_delete_evidence,
    risk_portal_download_evidence,
    risk_portal_approve_treatment,
    risk_portal_reject_treatment,
} from '../../../../services';
import { Body, bodySmall } from '../../../../Constants/Fonts';
import { fmtDate, fmtDateTime, LIKELIHOOD_LABELS, IMPACT_LABELS, titleCase, evidenceDisplayName } from './constants';

const APPROVER_ROLES = ['risk_reviewer', 'business_owner', 'ciso', 'risk_admin'];

export default function EvidenceSection({ riskId, detail, role, reload, notify, rescoreSlot = null }) {
    const [busy, setBusy] = useState(false);
    const { confirm, confirmModal } = useConfirm();
    const evidence = detail.evidence || [];
    const treatments = detail.treatments || [];
    const canApprove = APPROVER_ROLES.includes(role);

    const run = async (fn, ok) => {
        setBusy(true);
        try { await fn(); if (ok) notify(ok, 'success'); await reload(); }
        catch (e) { notify(e?.response?.data?.error?.message || e?.message || 'Action failed', 'error'); }
        finally { setBusy(false); }
    };

    const a = detail.assessment || {};
    const scaleVal = (n, labels) => (n >= 1 && n <= labels.length ? `${n} : ${labels[n - 1]}` : (n != null ? String(n) : undefined));
    // Short values → left : right chips.
    const assessmentItems = [
        ['Category', detail.risk_type ? titleCase(detail.risk_type) : undefined],
        ['Risk Owner', detail.risk_owner],
        ['Identification Date', detail.target_date ? fmtDate(detail.target_date) : undefined],
        ['State', detail.state ? (detail.state_label || titleCase(detail.state)) : undefined],
        ['Business Unit', detail.business_unit_name || (detail.business_unit ? String(detail.business_unit) : undefined)],
        ['Project', detail.project_name],
        ['Inherent Likelihood', scaleVal(a.inherent_likelihood ?? detail.likelihood, LIKELIHOOD_LABELS)],
        ['Inherent Impact', scaleVal(a.inherent_impact ?? detail.impact, IMPACT_LABELS)],
        ['Residual Likelihood', scaleVal(a.residual_likelihood, LIKELIHOOD_LABELS)],
        ['Residual Impact', scaleVal(a.residual_impact, IMPACT_LABELS)],
        ['Control Effectiveness', a.control_effectiveness != null ? `${a.control_effectiveness}%` : undefined],
        ['Risk Velocity', a.risk_velocity ? titleCase(a.risk_velocity) : undefined],
    ].filter(([, v]) => v != null && v !== '');
    // Long text values → full-width label-on-top blocks.
    const assessmentLongItems = [
        ['Risk Description', detail.risk],
        ['Threat', detail.threat],
        ['Vulnerability', detail.vulnerability],
        ['Notes', a.notes],
    ].filter(([, v]) => v != null && v !== '');

    // ── Treatment planning data — read-only list with approve/reject ────────
    const approve = (t) => run(() => risk_portal_approve_treatment(t.id), 'Treatment approved');
    const reject = (t) => {
        const reason = window.prompt('Reason for sending the plan back for revision:');
        if (reason === null) return;
        if (!reason.trim()) { notify('A reason is required to reject', 'error'); return; }
        run(() => risk_portal_reject_treatment(t.id, { reason }), 'Treatment sent back for revision');
    };
    // Treatment plans render as professional label : value cards (not a table) —
    // one card per plan, each a definition list with the reviewer's approve /
    // reject actions in the card footer.
    const treatmentCard = (t) => {
        const actionable = canApprove && t.status && !['approved', 'rejected'].includes(t.status);
        // String values render through <Body> so their colour comes from the
        // central BODY_FAMILY default (Fonts.js), not a per-cell override. Badge
        // nodes keep their own colour.
        const kv = [
            ['Strategy', <Body as="span" className="rp1_badge rp1_badge--info">{titleCase(t.strategy)}</Body>],
            ['Target Date', <Body as="span">{t.target_date ? fmtDate(t.target_date) : '—'}</Body>],
            
            ['Description', <Body as="span">{t.description || '—'}</Body>],
        ];
        return (
            <div key={t.id} className="rp1_tp_card">
                {kv.map(([label, value]) => (
                    <div key={label} className="rp1_tp_row">
                        <span className="rp1_tp_label">{label}</span>
                        <span className="rp1_tp_value">{value}</span>
                    </div>
                ))}
                {actionable && (
                    <div className="rp1_tp_actions">
                        <CommonButton variant="success" text="Approve" onClick={() => approve(t)} disable={busy} />
                        <CommonButton variant="error" text="Reject" onClick={() => reject(t)} disable={busy} />
                    </div>
                )}
            </div>
        );
    };

    // ── Evidence files — list / delete ──────────────────────────────────────
    const remove = async (ev) => {
        const ok = await confirm({ title: 'Delete evidence', message: `Delete "${ev.file_name}"?`, confirmLabel: 'Delete', danger: true, themeColor: false });
        if (ok) run(() => risk_portal_delete_evidence(ev.id), 'Evidence deleted');
    };

    const [previewing, setPreviewing] = useState(null);
    const preview = async (ev) => {
        const tab = window.open('', '_blank');
        setPreviewing(ev.id);
        try {
            // SbomApiIntercepter returns the raw body under `statusCode` (not `data`).
            const res = await risk_portal_download_evidence(ev.id);
            const blob = res?.statusCode instanceof Blob ? res.statusCode : new Blob([res?.statusCode]);
            // A blob-typed endpoint can still answer with a JSON error payload.
            if (blob.type === 'application/json') {
                if (tab) tab.close();
                const json = JSON.parse(await blob.text());
                notify(json?.error?.message || json?.message || 'Could not open this file', 'error');
                return;
            }
            const url = URL.createObjectURL(blob);
            if (tab) { tab.location.href = url; } else { window.open(url, '_blank'); }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            if (tab) tab.close();
            notify(e?.response?.data?.error?.message || e?.message || 'Could not open this file', 'error');
        } finally {
            setPreviewing(null);
        }
    };

    const rows = evidence.map((ev) => ({
        data: [
            <Body as="span" color="var(--color-text-primary)" weight="semibold">{evidenceDisplayName(ev) || '—'}</Body>,
            <Body as="span" variant={2} color="var(--color-text-muted)">{fmtDateTime(ev.uploaded_at)}</Body>,
            // Row actions as ICON buttons (preview / delete) — see CommonActionMenu's iconOnly.
            <CommonActionMenu
                iconOnly
                actions={[
                    { icon: 'preview', title: previewing === ev.id ? 'Opening…' : 'Preview', onClick: () => preview(ev), disabled: previewing === ev.id },
                    { icon: 'delete', title: 'Delete', danger: true, onClick: () => remove(ev), disabled: busy },
                ]}
            />,
        ],
    }));

    return (
        <>
            {/* Review stage — 2×2 grid: Risk Assessment | Treatment Plans on top,
                Evidence | Re-score below. Collapses to one column on narrow
                screens (see .rp1_review_grid). */}
            <div className="rp1_review_grid">
                {/* Assessment-stage data — read-only details panel (Jira-style:
                    label on top, value below; long text fields stacked full-width). */}
                <Section title="Risk Assessment">
                    {assessmentItems.length === 0 && assessmentLongItems.length === 0 ? (
                        <p style={bodySmall({ color: 'var(--color-text-muted)', margin: 0 })}>No assessment data yet.</p>
                    ) : (
                        <div>
                            {assessmentItems.length > 0 && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', columnGap: 32, rowGap: 18 }}>
                                    {assessmentItems.map(([label, value]) => (
                                        <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
                                            <span style={FIELD_LABEL}>{label}</span>
                                            <Body as="span" style={{ fontSize: 14, wordBreak: 'break-word' }}>{value}</Body>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {assessmentLongItems.length > 0 && (
                                <div style={{
                                    display: 'flex', flexDirection: 'column', gap: 18,
                                    ...(assessmentItems.length > 0 ? { marginTop: 20 } : {}),
                                }}>
                                    {assessmentLongItems.map(([label, value]) => (
                                        <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <span style={FIELD_LABEL}>{label}</span>
                                            <Body as="span" style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{value}</Body>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </Section>

                {/* Treatment planning data — label:value cards; reviewer can approve/reject. */}
                <Section title="Treatment Plans" count={treatments.length}>
                    {treatments.length === 0 ? (
                        <p style={bodySmall({ color: 'var(--color-text-muted)', margin: 0 })}>No treatment plans to review.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {treatments.map(treatmentCard)}
                        </div>
                    )}
                </Section>

                {/* Evidence files — action column uses icon buttons. */}
                <Section title="Evidence" count={evidence.length}>
                    {evidence.length === 0 ? (
                        <p style={bodySmall({ color: 'var(--color-text-muted)', margin: 0 })}>No evidence files attached.</p>
                    ) : (
                        <CommonTable headers={['Name', 'Uploaded Date', 'Action']} rows={rows} noPadding noScroll />
                    )}
                </Section>

                {/* Re-score block (Review stage) — supplied by the parent so it sits
                    beside Evidence in the grid's bottom-right cell. */}
                {rescoreSlot}
            </div>
            {confirmModal}
        </>
    );
}

// Shared field-label style for the read-only details panel — a plain, muted
// grey caption (no uppercase), matching the label column of the treatment cards.
const FIELD_LABEL = {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
};
